import { HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { HttpUtils, IAuthenticationService, nptAuthServiceToken, NptSnackBar, NptSpinnerService } from "@npt/sdk";
import { catchError, firstValueFrom, Observable, of } from "rxjs";

import { nptConf } from "../app.config";
import { Customer } from "../interface/customer";
import { CommonService } from "./common/common.service";

@Injectable({
  providedIn: 'root'
})
export class CustomerInfoService extends CommonService {
  private _customerId = signal<string | null>(null);
  private _customerCache: Customer | null = null;
  private _snackBar = inject(NptSnackBar);
  private _spinner = inject(NptSpinnerService);
  private _authService: IAuthenticationService = inject(nptAuthServiceToken);
  override _apiUrl = this._configurationService.config.beURL + '/api/odoo';

  get customerId(): string | null {
    return this._customerId();
  }

  public async setUserAttribute(): Promise<void> {
    const isLogged = await this._authService.isLoggedIn();
    if (isLogged) {
      const user = await this._authService.loadUserProfile();
      // ritorna, se è attiva l'autenticazione, il customerId preso da keycloak,
      // se non è presente utilizza fiscalCode o vat per recupare il customerId, altrimenti ritorna un valore fisso

      if (user['customerId'] && nptConf.securityType === 'oauth2') {
        this._customerId.set(user['customerId']);
      } else if (user['fiscalCode'] && nptConf.securityType === 'oauth2') {
        this.setCustomerId({ key: 'fiscalCode', value: user['fiscalCode'] });
      } else if (user['vat'] && nptConf.securityType === 'oauth2') {
        this.setCustomerId({ key: 'vat', value: user['vat'] });
      } else if (nptConf.securityType === 'noAuth') {
        this._customerId.set('P/00017');
      } else {
        this._snackBar.error('Attributi non presenti su keycloak');
      }
    }
  }

  public getCustomer(): Observable<Customer> {
    if (this._customerCache) {
      return of(this._customerCache);
    }

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      params: HttpUtils.createHttpParams({ customerId: this.customerId })
    };
    return this._httpClient.get<Customer>(this._apiUrl + '/getCustomer', options)
      .pipe(catchError(err => { throw err; }));
  }

  public clearCustomerCache(): void {
    this._customerCache = null;
  }

  private async setCustomerId(attribute: { key: string, value: string }): Promise<void> {
    try {
      this._spinner.show();
      const customer = await firstValueFrom(this.getCustomerWithKey(attribute));
      this._customerCache = customer;
      this._customerId.set(customer.customerId);
    } finally {
      this._spinner.hide();
    }
  }

  private getCustomerWithKey(attributes: { key: string, value: string }): Observable<Customer> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      params: new HttpParams().set(attributes.key, attributes.value)
    };
    return this._httpClient.get<Customer>(this._apiUrl + '/getCustomer', options)
      .pipe(catchError(err => { throw err; }));
  }
}
